import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['from-sign-up-to-a-real-fire-plan'];

export default function FromSignUpToARealFirePlanPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['your-daily-freedom-number', 'connect-everything-even-the-weird-stuff']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'Per Diem onboarding is designed to get a household to a usable plan quickly, not to force perfect setup on day one.',
          'Connected accounts help, but they are not a prerequisite for getting value from the product.',
          'The onboarding flow works because it sequences decisions in the order a real household would make them.',
        ]}
      />

      <ArticleSection title="Most financial onboarding breaks because it asks for everything up front">
        <ArticleParagraph>
          Many finance products say they want to help you plan, then immediately hand
          you a giant setup project. You are expected to link every account, clean every
          transaction, remember every recurring expense, and rebuild your household from
          scratch before you see anything useful.
        </ArticleParagraph>
        <ArticleParagraph>
          That approach is logically tidy, but it is bad product design. Real people want
          an answer first. They want to know whether the tool understands their situation
          and whether it can produce something worth refining.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem takes the opposite approach. The first job of onboarding is to get you
          to an understandable plan. Richer data comes next.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.onboardingConnect}
        alt="Per Diem mobile onboarding screen for connecting accounts"
        caption="The setup path starts by getting the household oriented, then lets connected accounts make the model richer when the user is ready."
        variant="phone"
      />

      <ArticleSection title="The flow is built around planning momentum">
        <ArticleParagraph>
          The easiest way to think about Per Diem onboarding is that it walks a household
          through the main pieces of its financial story in a sensible order.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'Create the account and set up the household context.',
            'Optionally connect financial accounts if you want live balances and transactions immediately.',
            'Review transactions and income only to the degree needed for the model to make sense.',
            'Sanity-check the budget, assets, family context, and upcoming life events.',
            'Choose the assumptions that matter for FIRE planning, then calculate.',
          ]}
        />
        <ArticleParagraph>
          That order matters. It means the product is not treating onboarding like data collection.
          It is treating onboarding like the first draft of a decision-making system.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileBudget}
        alt="Per Diem mobile budget screen"
        caption="Budgets are part of the setup because spending assumptions should feed the plan, not live in a separate tool."
        variant="phone"
      />

      <ArticleSection title="Account linking is helpful, but it is not the whole story">
        <ArticleParagraph>
          A lot of products implicitly assume that “onboarding” means “connect every account.”
          That is a useful step, but it is only one part of the job.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem is opinionated about something else: even with incomplete data, a household
          should be able to move forward. If you want to start by entering a simpler picture,
          you still should be able to see how spending, assets, income, and assumptions affect
          the long-term plan.
        </ArticleParagraph>
        <ArticleParagraph>
          That is especially important for people with messy finances, unusual assets, or a pile
          of accounts they are not ready to clean up in one sitting.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileHome}
        alt="Per Diem mobile home screen"
        caption="The product becomes useful quickly because the model can give the household a point of view before every last detail is perfect."
        variant="phone"
      />

      <ArticleSection title="Why the lighter setup path matters">
        <ArticleParagraph>
          Good onboarding is not about making a form shorter. It is about reducing the feeling
          that the user has to earn the product’s value before they are allowed to see it.
        </ArticleParagraph>
        <ArticleParagraph>
          In Per Diem, that means a new household can move from sign up to a FIRE plan without
          pretending that all of their financial data is already organized. The product can then
          get richer as accounts sync, categories improve, and life events become more specific.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="The household context is part of onboarding too">
        <ArticleParagraph>
          FIRE planning breaks when the app treats the household like a lone spreadsheet owner.
          A realistic setup has to account for partners, kids, future expenses, job changes,
          and other events that shape what freedom looks like.
        </ArticleParagraph>
        <ArticleParagraph>
          That is why onboarding in Per Diem includes family and life-event context rather than
          stopping at linked accounts and transaction categories. The plan is supposed to reflect
          real life, not just balances.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.addPartner}
        alt="Per Diem add partner screen"
        caption="A financial plan is stronger when the household itself is represented clearly, not just the accounts."
      />

      <ArticleSection title="The right goal is a believable first draft">
        <ArticleParagraph>
          A useful planning product should help a household arrive at a believable first draft
          quickly, then make it easier to improve that draft over time. That is the real promise
          of the onboarding flow.
        </ArticleParagraph>
        <ArticleParagraph>
          If the household wants a lower-friction way to answer “Where are we headed?” and
          “What should we tighten up next?” the product should meet them there first. That is
          what Per Diem is trying to do.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
